import React, { useState, useEffect } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/eye-slash.svg";
import { getRoleNames, getOrder, createPolicy } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";

const CreatePolicy = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [roleNames, setRoleNames] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [order, setOrder] = useState({});
  const { id } = useParams();

  const [initialValues, setInitialValues] = useState({
    status: "",
    // effective_date: "",
    // expiration_date: "",
    policy_start_date: "",
    policy_end_date: "",
    subscription_plan_name: "",
  });

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else {
      console.error("No ID provided in URL parameters");
    }
  }, [id]);

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await getOrder({ id: id });
      if (response.data) {
        setOrder(response.data);
        const fetchedValues = {
          status: "",
          // effective_date: "",
          // expiration_date: "",
          policy_start_date: "",
          policy_end_date: "",
          subscription_plan_name: response.data.insurance_plan,
        };
        setInitialValues(fetchedValues);
      } else {
        setOrder({});
        console.error("No data found for the given ID");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setOrder({});
    } finally {
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    status: Yup.string().required("Status is required"),
    // effective_date: Yup.date().required("Effective date is required"),
    // expiration_date: Yup.date().required("Expiration date is required"),
    policy_start_date: Yup.date().required("Policy start date is required"),
    policy_end_date: Yup.date().required("Policy end date is required"),
    subscription_plan_name: Yup.string()
      .required("Subscription plan name is required")
      .min(3, "Subscription plan name must be at least 3 characters")
      .max(50, "Subscription plan name must be at most 50 characters"),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      if (!order || Object.keys(order).length === 0) {
        throw new Error("Order data is not available");
      }
      const payload = {
        ...values,
        user_id: order.user_id,
        order_id: order.id,
      };
      await createPolicy(payload);
      toast.success("Policy created successfully!");
      navigate("/insurance-policies");
    } catch (error) {
      console.error("Failed to create policy:", error);
      toast.error(
        error.message || "Failed to create policy. Please try again."
      );
      setErrors({ submit: "Failed to create policy", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "expired", label: "Expired" },
  ];

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  {/* Form fields */}
                  <div className="row">
                    {/* Subscription Plan Name */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="subscription_plan_name" className="form-label">
                        <span className="text-danger">*</span> Subscription Plan Name
                      </label>
                      <Field
                        name="subscription_plan_name"
                        as="input"
                        type="text"
                        className={`form-control ${touched.subscription_plan_name && errors.subscription_plan_name
                            ? "is-invalid"
                            : ""
                          }`}
                      />
                      <ErrorMessage
                        name="subscription_plan_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Status */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="status" className="form-label">
                        <span className="text-danger">*</span> Status
                      </label>
                      <Field
                        name="status"
                        as="select"
                        className={`form-control ${touched.status && errors.status ? "is-invalid" : ""
                          }`}
                      >
                        <option value="">Select status</option>
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  {/* <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="effective_date" className="form-label">
                        <span className="text-danger">*</span> Effective Date
                      </label>
                      <Field
                        name="effective_date"
                        as="input"
                        type="date"
                        className={`form-control ${
                          touched.effective_date && errors.effective_date
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="effective_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="expiration_date" className="form-label">
                        <span className="text-danger">*</span> Expiration Date
                      </label>
                      <Field
                        name="expiration_date"
                        as="input"
                        type="date"
                        className={`form-control ${
                          touched.expiration_date && errors.expiration_date
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="expiration_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div> */}
                  <div className="row">
                    {/* Policy Start Date */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="policy_start_date" className="form-label">
                        <span className="text-danger">*</span> Policy Start Date
                      </label>
                      <Field
                        name="policy_start_date"
                        as="input"
                        type="date"
                        className={`form-control ${touched.policy_start_date && errors.policy_start_date
                            ? "is-invalid"
                            : ""
                          }`}
                      />
                      <ErrorMessage
                        name="policy_start_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Policy End Date */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="policy_end_date" className="form-label">
                        <span className="text-danger">*</span> Policy End Date
                      </label>
                      <Field
                        name="policy_end_date"
                        as="input"
                        type="date"
                        className={`form-control ${touched.policy_end_date && errors.policy_end_date
                            ? "is-invalid"
                            : ""
                          }`}
                      />
                      <ErrorMessage
                        name="policy_end_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={() => navigate("/orders")}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Create Policy
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default CreatePolicy;
